import { useState } from 'react';

type PayMethod = 'wechat' | 'card' | 'paypal';
type BillingCycle = 'monthly' | 'quarterly' | 'yearly';

interface PaymentModalProps {
  plan: 'basic' | 'gold' | 'reseller';
  onClose: () => void;
}

const PRICES = {
  basic: { monthly: 5, quarterly: 13, yearly: 48 },
  gold: { monthly: 10, quarterly: 28, yearly: 108 },
  reseller: { monthly: 0, quarterly: 0, yearly: 0 },
};

const BILLING_LABELS: Record<BillingCycle, string> = {
  monthly: '月付',
  quarterly: '季付（省15%）',
  yearly: '年付（省33%）',
};

export default function PaymentModal({ plan, onClose }: PaymentModalProps) {
  const [method, setMethod] = useState<PayMethod>('wechat');
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const [step, setStep] = useState<'select' | 'pay' | 'success'>('select');
  const [loading, setLoading] = useState(false);
  const [cardData, setCardData] = useState({ number: '', name: '', expiry: '', cvv: '' });
  const [paypalEmail, setPaypalEmail] = useState('');
  const [agreed, setAgreed] = useState(false);

  const price = PRICES[plan][billing];
  const planName = plan === 'gold' ? '黄金会员' : plan === 'reseller' ? '分销商会员' : '工厂会员';

  const formatCard = (val: string) => val.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim().slice(0, 19);
  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, '');
    if (digits.length >= 2) return digits.slice(0, 2) + '/' + digits.slice(2, 4);
    return digits;
  };

  const handlePay = async () => {
    if (!agreed) return;
    setLoading(true);
    const body = new URLSearchParams();
    body.append('form_type', '会员付款');
    body.append('plan', planName);
    body.append('billing_cycle', plan === 'reseller' ? '永久免费' : BILLING_LABELS[billing]);
    body.append('amount', plan === 'reseller' ? '免费' : `¥${price}`);
    body.append('payment_method', plan === 'reseller' ? '免费注册' : method === 'wechat' ? '微信支付' : method === 'card' ? '银行卡/信用卡' : 'PayPal');
    if (method === 'paypal') body.append('paypal_email', paypalEmail);
    try {
      await fetch('https://readdy.ai/api/form/d7d688nsch5bvrijfccg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setTimeout(() => { setLoading(false); setStep('success'); }, 1200);
    } catch {
      setLoading(false);
    }
  };

  const headerGradient = plan === 'gold'
    ? 'bg-gradient-to-r from-amber-500 to-yellow-500'
    : plan === 'reseller'
    ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
    : 'bg-gradient-to-r from-red-600 to-red-700';

  const headerIcon = plan === 'gold' ? 'ri-vip-crown-line' : plan === 'reseller' ? 'ri-share-line' : 'ri-building-line';

  const accentColor = plan === 'gold' ? 'text-amber-600' : plan === 'reseller' ? 'text-emerald-600' : 'text-red-600';
  const accentBg = plan === 'gold' ? 'bg-amber-50 border-amber-100' : plan === 'reseller' ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100';
  const btnClass = plan === 'gold'
    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600'
    : plan === 'reseller'
    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600'
    : 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800';

  // Reseller: show a simple free registration confirmation
  if (plan === 'reseller') {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div
          className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`p-6 ${headerGradient}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className={`${headerIcon} text-white text-xl`}></i>
                </div>
                <div>
                  <div className="text-white font-bold text-lg">{planName}</div>
                  <div className="text-white/70 text-xs">富缘佳旺 FortuneMatch</div>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          {step === 'success' ? (
            <div className="p-8 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                <i className="ri-check-double-line text-emerald-500 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">申请成功！</h3>
              <p className="text-gray-500 text-sm mb-2">恭喜您成为富缘佳旺 <strong className="text-emerald-600">分销商会员</strong></p>
              <p className="text-gray-400 text-xs mb-6">我们将在1-3个工作日内审核您的申请，审核通过后会通过邮件通知您。</p>
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-6 text-left">
                {[
                  { icon: 'ri-gift-line', text: '分销商会员资格永久免费' },
                  { icon: 'ri-money-cny-circle-line', text: '专属佣金体系，推广即赚钱' },
                  { icon: 'ri-dashboard-line', text: '实时佣金追踪后台' },
                  { icon: 'ri-customer-service-line', text: '专属分销商客服支持' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-gray-600 mb-2 last:mb-0">
                    <i className={`${item.icon} text-emerald-500`}></i>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
              <button onClick={onClose} className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-full font-semibold text-sm hover:from-emerald-600 hover:to-teal-600 transition-all cursor-pointer whitespace-nowrap">
                好的，期待审核通过！
              </button>
            </div>
          ) : (
            <div className="p-6">
              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-gift-2-line text-emerald-500 text-lg"></i>
                  <span className="font-bold text-emerald-700 text-base">分销商会员 · 永久免费</span>
                </div>
                <p className="text-xs text-emerald-600 leading-relaxed">
                  分销商会员完全免费，通过推广平台赚取佣金。申请后我们将在1-3个工作日内审核。
                </p>
              </div>

              <div className="flex flex-col gap-3 mb-5">
                {[
                  { icon: 'ri-percent-line', label: '佣金比例', value: '最高30%' },
                  { icon: 'ri-time-line', label: '结算周期', value: '每月15日结算' },
                  { icon: 'ri-paypal-line', label: '收款方式', value: 'PayPal / 银行转账' },
                  { icon: 'ri-user-heart-line', label: '会员费用', value: '永久免费' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <i className={`${item.icon} text-emerald-500`}></i>
                      {item.label}
                    </div>
                    <span className="text-sm font-semibold text-gray-800">{item.value}</span>
                  </div>
                ))}
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 accent-emerald-600"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  我已阅读并同意
                  <a href="#" className="text-emerald-500 mx-1 cursor-pointer">《分销商服务协议》</a>
                  和
                  <a href="#" className="text-emerald-500 mx-1 cursor-pointer">《佣金结算规则》</a>
                </span>
              </label>

              <button
                onClick={handlePay}
                disabled={loading || !agreed}
                className={`w-full py-4 rounded-full font-bold text-base transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${btnClass}`}
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin mr-2"></i>提交中...</>
                ) : (
                  <><i className="ri-share-line mr-2"></i>免费申请分销商会员</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {/* Header */}
        <div className={`p-6 ${headerGradient}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                <i className={`${headerIcon} text-white text-xl`}></i>
              </div>
              <div>
                <div className="text-white font-bold text-lg">{planName}</div>
                <div className="text-white/70 text-xs">富缘佳旺 FortuneMatch</div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        {step === 'success' ? (
          <div className="p-8 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-double-line text-green-500 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">支付成功！</h3>
            <p className="text-gray-500 text-sm mb-2">恭喜您成为富缘佳旺 <strong className={accentColor}>{planName}</strong></p>
            <p className="text-gray-400 text-xs mb-6">您的会员权益已立即生效，开始您的缘分之旅吧！</p>
            <div className={`rounded-xl p-4 mb-6 border ${accentBg}`}>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">会员类型</span>
                <span className="font-semibold text-gray-800">{planName}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">计费周期</span>
                <span className="font-semibold text-gray-800">{BILLING_LABELS[billing]}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">支付金额</span>
                <span className={`font-bold text-lg ${accentColor}`}>¥{price}</span>
              </div>
            </div>
            <button onClick={onClose} className={`w-full py-3 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${btnClass}`}>
              开始使用会员权益
            </button>
          </div>
        ) : (
          <div className="p-6">
            {/* Factory member free trial notice */}
            {plan === 'basic' && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 mb-5 flex items-start gap-2">
                <i className="ri-gift-line text-red-500 mt-0.5"></i>
                <div>
                  <div className="text-sm font-semibold text-red-700">前3个月完全免费！</div>
                  <div className="text-xs text-red-500 mt-0.5">第4个月起自动转为 ¥5/月，届时提前通知您</div>
                </div>
              </div>
            )}

            {/* Billing Cycle */}
            <div className="mb-5">
              <label className="text-xs font-semibold text-gray-500 mb-2 block">选择计费周期</label>
              <div className="grid grid-cols-3 gap-2">
                {(['monthly', 'quarterly', 'yearly'] as BillingCycle[]).map((b) => (
                  <button
                    key={b}
                    onClick={() => setBilling(b)}
                    className={`py-2.5 rounded-xl text-xs font-semibold border-2 transition-all cursor-pointer ${billing === b ? (plan === 'gold' ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-red-500 bg-red-50 text-red-700') : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                  >
                    <div className="font-bold text-base">¥{PRICES[plan][b]}</div>
                    <div className="text-[10px] mt-0.5">{b === 'monthly' ? '月付' : b === 'quarterly' ? '季付' : '年付'}</div>
                    {b !== 'monthly' && (
                      <div className={`text-[10px] mt-0.5 font-semibold ${plan === 'gold' ? 'text-amber-500' : 'text-red-500'}`}>
                        {b === 'quarterly' ? '省15%' : '省33%'}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className={`rounded-xl p-4 mb-5 border ${accentBg}`}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-semibold text-gray-800">{planName} · {BILLING_LABELS[billing]}</div>
                  <div className="text-xs text-gray-500 mt-0.5">富缘佳旺 FortuneMatch.com</div>
                </div>
                <div className={`text-2xl font-bold ${accentColor}`}>¥{price}</div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="mb-5">
              <label className="text-xs font-semibold text-gray-500 mb-2 block">选择支付方式</label>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { id: 'wechat' as PayMethod, icon: 'ri-wechat-pay-line', label: '微信支付', color: 'text-green-600', activeBg: 'bg-green-50 border-green-400' },
                  { id: 'card' as PayMethod, icon: 'ri-bank-card-line', label: '银行卡', color: 'text-gray-700', activeBg: 'bg-gray-50 border-gray-400' },
                  { id: 'paypal' as PayMethod, icon: 'ri-paypal-line', label: 'PayPal', color: 'text-sky-600', activeBg: 'bg-sky-50 border-sky-400' },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setMethod(m.id)}
                    className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-all cursor-pointer ${method === m.id ? m.activeBg : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <i className={`${m.icon} text-2xl ${method === m.id ? m.color : 'text-gray-400'}`}></i>
                    <span className={`text-xs font-medium ${method === m.id ? 'text-gray-800' : 'text-gray-400'}`}>{m.label}</span>
                  </button>
                ))}
              </div>

              {method === 'wechat' && (
                <div className="bg-green-50 border border-green-100 rounded-xl p-5 text-center">
                  <div className="w-32 h-32 bg-white rounded-xl mx-auto mb-3 flex items-center justify-center border border-green-200">
                    <div className="text-center">
                      <i className="ri-qr-code-line text-5xl text-gray-300"></i>
                      <div className="text-xs text-gray-400 mt-1">微信扫码</div>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-green-700 mb-1">请使用微信扫描二维码支付</p>
                  <p className="text-xs text-green-600">支付金额：<strong>¥{price}</strong></p>
                  <div className="flex items-center justify-center gap-2 mt-3 text-xs text-gray-400">
                    <i className="ri-lock-line text-green-500"></i>
                    <span>微信支付安全加密，放心支付</span>
                  </div>
                </div>
              )}

              {method === 'card' && (
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2 mb-1">
                    {['ri-visa-line', 'ri-mastercard-line', 'ri-bank-card-line'].map((ic, i) => (
                      <div key={i} className="w-10 h-7 flex items-center justify-center bg-gray-100 rounded border border-gray-200">
                        <i className={`${ic} text-gray-500 text-sm`}></i>
                      </div>
                    ))}
                    <span className="text-xs text-gray-400 self-center ml-1">支持 Visa / Mastercard / 银联</span>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1.5 block">卡号</label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      value={cardData.number}
                      onChange={(e) => setCardData({ ...cardData, number: formatCard(e.target.value) })}
                      maxLength={19}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 font-mono tracking-wider"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium mb-1.5 block">持卡人姓名</label>
                    <input
                      type="text"
                      placeholder="ZHANG SAN"
                      value={cardData.name}
                      onChange={(e) => setCardData({ ...cardData, name: e.target.value.toUpperCase() })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 uppercase"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 font-medium mb-1.5 block">有效期</label>
                      <input
                        type="text"
                        placeholder="MM/YY"
                        value={cardData.expiry}
                        onChange={(e) => setCardData({ ...cardData, expiry: formatExpiry(e.target.value) })}
                        maxLength={5}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 font-mono"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium mb-1.5 block">CVV 安全码</label>
                      <input
                        type="password"
                        placeholder="•••"
                        value={cardData.cvv}
                        onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                        maxLength={4}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 font-mono"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-50 rounded-lg p-2.5">
                    <i className="ri-lock-line text-green-500"></i>
                    <span>您的卡片信息经过256位SSL加密保护，安全可靠</span>
                  </div>
                </div>
              )}

              {method === 'paypal' && (
                <div className="bg-sky-50 border border-sky-100 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-paypal-line text-sky-600 text-2xl"></i>
                    <span className="font-bold text-sky-700 text-lg">PayPal</span>
                  </div>
                  <div className="mb-3">
                    <label className="text-xs text-gray-500 font-medium mb-1.5 block">PayPal 邮箱地址</label>
                    <input
                      type="email"
                      placeholder="your@paypal.com"
                      value={paypalEmail}
                      onChange={(e) => setPaypalEmail(e.target.value)}
                      className="w-full border border-sky-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-sky-400 bg-white"
                    />
                  </div>
                  <p className="text-xs text-sky-600 leading-relaxed">
                    点击支付后，您将跳转至 PayPal 安全页面完成付款。支持全球主要货币，自动换算为人民币。
                  </p>
                  <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
                    <i className="ri-shield-check-line text-sky-500"></i>
                    <span>PayPal 买家保护，安全有保障</span>
                  </div>
                </div>
              )}
            </div>

            <label className="flex items-start gap-2.5 cursor-pointer mb-5">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 accent-red-600"
              />
              <span className="text-xs text-gray-500 leading-relaxed">
                我已阅读并同意
                <a href="#" className="text-red-500 mx-1 cursor-pointer">《会员服务协议》</a>
                和
                <a href="#" className="text-red-500 mx-1 cursor-pointer">《自动续费说明》</a>
                。订阅将按所选周期自动续费，可随时在账户设置中取消。
              </span>
            </label>

            <button
              onClick={handlePay}
              disabled={loading || !agreed}
              className={`w-full py-4 rounded-full font-bold text-base transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${btnClass}`}
            >
              {loading ? (
                <><i className="ri-loader-4-line animate-spin mr-2"></i>支付处理中...</>
              ) : (
                <><i className="ri-secure-payment-line mr-2"></i>确认支付 ¥{price}</>
              )}
            </button>

            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400">
              <span className="flex items-center gap-1"><i className="ri-lock-line text-green-500"></i>SSL加密</span>
              <span className="flex items-center gap-1"><i className="ri-shield-check-line text-green-500"></i>安全支付</span>
              <span className="flex items-center gap-1"><i className="ri-refund-2-line text-green-500"></i>7天退款</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
